import Phaser from 'phaser';
import {
  COLORS,
  DEPTHS,
  FONTS,
  GAME_HEIGHT,
  GAME_WIDTH,
  toHexColor,
} from '../config/gameConfig';
import {
  AR_FLOOR_LESSONS,
  CORE_FLOOR_TERMS,
  FLOOR_TERM_COPY,
  type FloorChoice,
  type FloorLesson,
  type FloorStationId,
  type FloorTermId,
} from '../content/ar-floor-lessons';
import type { Term } from '../systems/EventBus';
import { SaveStore } from '../systems/SaveStore';
import { TopDownCurriculum, type LessonCompletion } from '../systems/TopDownCurriculum';

const FLOOR = {
  wall: 0x1e3558,
  wallTrim: 0x68e1d7,
  tileA: 0xf7d48b,
  tileB: 0xf2bd66,
  rug: 0x236f79,
  tray: 0xffcf3f,
  counter: 0xb87942,
  counterDark: 0x76502e,
  player: 0x2f80ed,
  playerAccent: 0xff6b6b,
  ink: 0x122033,
  muted: 0x4f6077,
  panel: 0xfffbf1,
  panelStroke: 0x27364d,
  success: 0x1f9d73,
  danger: 0xe14d4d,
  target: 0xffc857,
  shadow: 0x152033,
};

const STATIONS: readonly {
  id: FloorStationId;
  label: string;
  icon: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color: number;
}[] = [
  {
    id: 'invoice-desk',
    label: 'Invoice Desk',
    icon: 'file-text',
    x: 185,
    y: 315,
    width: 180,
    height: 118,
    color: 0x6c63ff,
  },
  {
    id: 'aging-board',
    label: 'Aging Board',
    icon: 'table',
    x: 512,
    y: 300,
    width: 198,
    height: 132,
    color: 0xf59e0b,
  },
  {
    id: 'collections-desk',
    label: 'Collections',
    icon: 'phone',
    x: 840,
    y: 315,
    width: 180,
    height: 118,
    color: 0xef4444,
  },
  {
    id: 'payments-desk',
    label: 'Payments',
    icon: 'circle-dollar-sign',
    x: 318,
    y: 510,
    width: 198,
    height: 124,
    color: 0x22c55e,
  },
  {
    id: 'resolution-desk',
    label: 'Resolution',
    icon: 'book-open',
    x: 706,
    y: 510,
    width: 198,
    height: 124,
    color: 0x14b8a6,
  },
];

const WORK_TRAY = {
  x: 106,
  y: 635,
  width: 170,
  height: 108,
};

const PLAYER_SPEED = 250;
const INTERACT_DISTANCE = 150;
const WORK_ITEM_PICKUP_DISTANCE = 118;

type FloorCharacter = 'blue' | 'pink';

interface ARFloorData {
  character?: FloorCharacter;
}

interface StationView {
  id: FloorStationId;
  x: number;
  y: number;
  width: number;
  height: number;
  color: number;
  container: Phaser.GameObjects.Container;
  outline: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text;
  targetRing: Phaser.GameObjects.Arc;
}

interface WorkItemView {
  lessonId: string;
  container: Phaser.GameObjects.Container;
}

const CORE_SAVE_TERMS: ReadonlySet<Term> = new Set<Term>([
  'aging-bucket',
  'dunning',
  'cash-application',
  'short-pay',
  'dispute',
  'promise-to-pay',
  'write-off',
  'dso',
]);

const isCoreSaveTerm = (term: FloorTermId): term is Term => CORE_SAVE_TERMS.has(term as Term);

const fmtLessonCount = (idx: number): string => `${Math.min(idx + 1, AR_FLOOR_LESSONS.length)}/${AR_FLOOR_LESSONS.length}`;

const displayTermTitle = (term: FloorTermId): string => {
  if (term === 'dso') return 'DSO';
  return FLOOR_TERM_COPY[term].title;
};

export class ARFloorScene extends Phaser.Scene {
  private curriculum = new TopDownCurriculum(AR_FLOOR_LESSONS);
  private saveStore = new SaveStore();

  private player!: Phaser.GameObjects.Container;
  private playerShadow!: Phaser.GameObjects.Ellipse;
  private playerBody!: Phaser.GameObjects.Arc;
  private playerFace!: Phaser.GameObjects.Arc;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private keys!: {
    W: Phaser.Input.Keyboard.Key;
    A: Phaser.Input.Keyboard.Key;
    S: Phaser.Input.Keyboard.Key;
    D: Phaser.Input.Keyboard.Key;
    Q: Phaser.Input.Keyboard.Key;
    SPACE: Phaser.Input.Keyboard.Key;
    E: Phaser.Input.Keyboard.Key;
    ESC: Phaser.Input.Keyboard.Key;
    ONE: Phaser.Input.Keyboard.Key;
    TWO: Phaser.Input.Keyboard.Key;
    THREE: Phaser.Input.Keyboard.Key;
  };

  private stations = new Map<FloorStationId, StationView>();
  private lessonPanel!: Phaser.GameObjects.Container;
  private glossaryPanel!: Phaser.GameObjects.Container;
  private actionText!: Phaser.GameObjects.Text;
  private toastText!: Phaser.GameObjects.Text;
  private targetMarker!: Phaser.GameObjects.Container;
  private currentItem: WorkItemView | null = null;
  private heldItem: WorkItemView | null = null;
  private modal: Phaser.GameObjects.Container | null = null;
  private modalChoices: readonly FloorChoice[] = [];
  private modalChoiceHandler: ((choice: FloorChoice) => void) | null = null;
  private debriefOverlay: Phaser.GameObjects.Container | null = null;
  private debriefContinue: (() => void) | null = null;
  private welcomeOverlay: Phaser.GameObjects.Container | null = null;
  private completionOverlay: Phaser.GameObjects.Container | null = null;
  private learnedTerms = new Set<FloorTermId>();
  private character: FloorCharacter = 'blue';
  private lastInteractDown = false;
  private lastDropDown = false;
  private lastEscDown = false;
  private runRecorded = false;

  constructor() {
    super({ key: 'ARFloorScene' });
  }

  create(data?: ARFloorData): void {
    this.character = data?.character ?? 'blue';
    this.curriculum = new TopDownCurriculum(AR_FLOOR_LESSONS);
    this.saveStore = new SaveStore();
    this.learnedTerms.clear();
    this.runRecorded = false;
    this.currentItem = null;
    this.heldItem = null;
    this.modal = null;
    this.debriefOverlay = null;
    this.debriefContinue = null;
    this.completionOverlay = null;

    this.createInput();
    this.createRoom();
    this.createStations();
    this.createPlayer();
    this.createHud();
    this.spawnCurrentWorkItem();
    this.refreshLessonPanel();
    this.refreshGlossaryPanel();
    this.showWelcomeOverlay();

    this.cameras.main.fadeIn(250, 255, 255, 255);
  }

  update(_time: number, delta: number): void {
    if (this.completionOverlay) return;
    if (this.welcomeOverlay) return;
    if (this.debriefOverlay) {
      this.handleDebriefShortcuts();
      return;
    }
    if (this.modal) {
      this.handleModalShortcuts();
    } else {
      this.updateMovement(delta);
      this.handleActions();
    }
    this.updateHeldItemPosition();
    this.updateStationHighlights();
    this.updateActionHint();
  }

  private createInput(): void {
    this.cursors = this.input.keyboard!.createCursorKeys();
    this.keys = {
      W: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.W),
      A: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.A),
      S: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.S),
      D: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.D),
      Q: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.Q),
      SPACE: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE),
      E: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.E),
      ESC: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.ESC),
      ONE: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.ONE),
      TWO: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.TWO),
      THREE: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.THREE),
    };
  }

  private createRoom(): void {
    this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, FLOOR.tileA).setOrigin(0, 0);

    const tile = 54;
    for (let y = 88; y < GAME_HEIGHT; y += tile) {
      for (let x = 0; x < GAME_WIDTH; x += tile) {
        if (((x / tile) + (y / tile)) % 2 === 0) {
          this.add.rectangle(x, y, tile, tile, FLOOR.tileB, 0.62).setOrigin(0, 0);
        }
      }
    }

    this.add.rectangle(0, 0, GAME_WIDTH, 88, FLOOR.wall).setOrigin(0, 0);
    this.add.rectangle(0, 84, GAME_WIDTH, 6, FLOOR.wallTrim).setOrigin(0, 0);

    this.add.text(28, 26, 'Fazeshift AR Lab', {
      fontFamily: FONTS.ui,
      fontSize: '27px',
      color: '#ffffff',
      fontStyle: '900',
    });
    this.add.text(30, 58, 'Move work through the room to learn how receivables become cash.', {
      fontFamily: FONTS.ui,
      fontSize: '13px',
      color: '#d7e3ee',
    });

    this.add.text(GAME_WIDTH - 28, 44, 'SPACE / E: interact   Q: drop   ESC: pause', {
      fontFamily: FONTS.ui,
      fontSize: '13px',
      color: '#ffffff',
      fontStyle: '800',
    }).setOrigin(1, 0.5);

    this.add.rectangle(GAME_WIDTH / 2, 425, 630, 270, FLOOR.rug, 0.16)
      .setStrokeStyle(2, 0x0f5965, 0.2);

    this.drawRoomProps();

    this.drawWorkTray();
  }

  private drawRoomProps(): void {
    this.add.rectangle(90, 170, 86, 52, 0x345f78).setStrokeStyle(2, 0x15384a);
    this.add.rectangle(90, 159, 74, 13, 0x74d3d0);
    this.add.text(90, 184, 'OPEN AR', {
      fontFamily: FONTS.ui,
      fontSize: '10px',
      color: '#ffffff',
      fontStyle: '900',
    }).setOrigin(0.5);

    for (let i = 0; i < 4; i++) {
      this.add.rectangle(925, 184 + i * 30, 68, 20, 0xffffff, 0.8)
        .setStrokeStyle(2, 0xd9b04f)
        .setRotation(Phaser.Math.FloatBetween(-0.08, 0.08));
    }

    this.add.rectangle(512, 374, 180, 34, 0x1f3f57, 0.28).setStrokeStyle(1, 0x7dd3fc, 0.25);
    this.add.text(512, 374, 'RUN WORK TO STATIONS', {
      fontFamily: FONTS.ui,
      fontSize: '12px',
      color: '#1f3f57',
      fontStyle: '900',
    }).setOrigin(0.5);
  }

  private drawWorkTray(): void {
    const glow = this.add.circle(WORK_TRAY.x, WORK_TRAY.y, 90)
      .setStrokeStyle(6, FLOOR.target, 0.42);
    this.tweens.add({ targets: glow, scale: 1.1, alpha: 0.45, duration: 680, yoyo: true, repeat: -1 });

    this.add.rectangle(WORK_TRAY.x + 6, WORK_TRAY.y + 9, WORK_TRAY.width, WORK_TRAY.height, FLOOR.shadow, 0.2)
      .setOrigin(0.5);
    this.add.rectangle(WORK_TRAY.x, WORK_TRAY.y, WORK_TRAY.width, WORK_TRAY.height, FLOOR.tray)
      .setStrokeStyle(5, 0x8a5a00)
      .setOrigin(0.5);
    this.add.text(WORK_TRAY.x, WORK_TRAY.y - 68, 'START HERE', {
      fontFamily: FONTS.ui,
      fontSize: '16px',
      color: toHexColor(FLOOR.ink),
      fontStyle: '900',
    }).setOrigin(0.5);
    this.add.text(WORK_TRAY.x, WORK_TRAY.y + 58, 'SPACE / E to pick up', {
      fontFamily: FONTS.ui,
      fontSize: '12px',
      color: toHexColor(FLOOR.ink),
      fontStyle: '900',
    }).setOrigin(0.5);
  }

  private createStations(): void {
    for (const cfg of STATIONS) {
      const c = this.add.container(cfg.x, cfg.y);

      const shadow = this.add.rectangle(7, 13, cfg.width, cfg.height, FLOOR.shadow, 0.22)
        .setOrigin(0.5);
      c.add(shadow);

      const base = this.add.rectangle(0, 18, cfg.width, cfg.height - 18, FLOOR.counterDark)
        .setOrigin(0.5);
      c.add(base);

      const body = this.add.rectangle(0, -8, cfg.width, cfg.height - 20, FLOOR.counter)
        .setStrokeStyle(3, cfg.color)
        .setOrigin(0.5);
      c.add(body);

      const accent = this.add.rectangle(0, -cfg.height / 2 + 9, cfg.width - 14, 16, cfg.color)
        .setOrigin(0.5);
      c.add(accent);

      const sign = this.add.rectangle(0, -cfg.height / 2 - 24, cfg.width - 28, 30, 0x102033)
        .setStrokeStyle(2, cfg.color)
        .setOrigin(0.5);
      c.add(sign);

      const icon = this.add.image(-cfg.width / 2 + 38, -10, `icon:${cfg.icon}`)
        .setDisplaySize(38, 38)
        .setTint(0xffffff);
      c.add(icon);

      const label = this.add.text(0, -cfg.height / 2 - 25, cfg.label, {
        fontFamily: FONTS.ui,
        fontSize: '15px',
        color: '#ffffff',
        fontStyle: '900',
      }).setOrigin(0.5);
      c.add(label);

      const hint = this.add.text(0, 28, 'Bring matching work here', {
        fontFamily: FONTS.ui,
        fontSize: '11px',
        color: '#fff5d8',
        fontStyle: '800',
      }).setOrigin(0.5);
      c.add(hint);

      if (cfg.id === 'aging-board') this.drawBucketMini(c, cfg.width);
      if (cfg.id === 'payments-desk') this.drawCashMini(c, cfg.width);

      const targetRing = this.add.circle(cfg.x, cfg.y, Math.max(cfg.width, cfg.height) / 2 + 13)
        .setStrokeStyle(7, FLOOR.target, 0)
        .setVisible(false);
      this.tweens.add({
        targets: targetRing,
        scale: 1.1,
        duration: 620,
        yoyo: true,
        repeat: -1,
      });

      this.stations.set(cfg.id, {
        id: cfg.id,
        x: cfg.x,
        y: cfg.y,
        width: cfg.width,
        height: cfg.height,
        color: cfg.color,
        container: c,
        outline: body,
        label,
        targetRing,
      });
    }
  }

  private drawBucketMini(container: Phaser.GameObjects.Container, width: number): void {
    const colors = [0x22c55e, 0xfbbf24, 0xfb923c, 0xef4444];
    const labels = ['0-30', '31-60', '61-90', '90+'];
    const startX = -width / 2 + 24;
    for (let i = 0; i < 4; i++) {
      const barH = 12 + i * 7;
      container.add(this.add.rectangle(startX + i * 36, 45 - barH / 2, 24, barH, colors[i]).setOrigin(0.5));
      container.add(this.add.text(startX + i * 36, 53, labels[i], {
        fontFamily: FONTS.ui,
        fontSize: '8px',
        color: '#fff9e8',
        fontStyle: '900',
      }).setOrigin(0.5, 0));
    }
  }

  private drawCashMini(container: Phaser.GameObjects.Container, width: number): void {
    const x = width / 2 - 42;
    container.add(this.add.circle(x, 36, 19, 0xdff8e8).setStrokeStyle(2, 0x22c55e));
    container.add(this.add.text(x, 36, '$', {
      fontFamily: FONTS.ui,
      fontSize: '22px',
      color: '#178b4c',
      fontStyle: '800',
    }).setOrigin(0.5));
  }

  private createPlayer(): void {
    this.player = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT - 132);
    this.player.setDepth(80);

    const outfit = this.character === 'pink' ? 0xec4899 : FLOOR.player;
    const outline = this.character === 'pink' ? 0x9f2b67 : 0x185abc;
    const hair = this.character === 'pink' ? 0x6b3e26 : 0x3b2f2f;

    this.playerShadow = this.add.ellipse(0, 30, 56, 20, FLOOR.shadow, 0.22);
    this.player.add(this.playerShadow);

    this.playerBody = this.add.circle(0, 4, 26, outfit);
    this.playerBody.setStrokeStyle(4, outline);
    this.player.add(this.playerBody);

    this.player.add(this.add.rectangle(0, 24, 38, 16, FLOOR.playerAccent).setOrigin(0.5));
    this.player.add(this.add.circle(0, -23, 18, 0xf6c7a9).setStrokeStyle(3, FLOOR.ink));
    this.player.add(this.add.ellipse(0, -36, 35, 15, hair));
    this.player.add(this.add.circle(-7, -25, 3, FLOOR.ink));
    this.playerFace = this.add.circle(8, -25, 3, FLOOR.ink);
    this.player.add(this.playerFace);
  }

  private createHud(): void {
    this.lessonPanel = this.add.container(24, 96).setDepth(120);
    this.glossaryPanel = this.add.container(GAME_WIDTH - 356, 96).setDepth(120);

    this.targetMarker = this.add.container(0, 0).setDepth(115).setVisible(false);
    this.targetMarker.add(this.add.triangle(0, -24, 0, -26, -16, 0, 16, 0, FLOOR.target)
      .setStrokeStyle(3, FLOOR.ink));
    this.targetMarker.add(this.add.text(0, 18, 'GO HERE', {
      fontFamily: FONTS.ui,
      fontSize: '14px',
      color: toHexColor(FLOOR.ink),
      fontStyle: '900',
      backgroundColor: '#ffcf3f',
      padding: { x: 8, y: 4 },
    }).setOrigin(0.5));
    this.tweens.add({
      targets: this.targetMarker,
      y: '+=10',
      duration: 560,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.actionText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 28, '', {
      fontFamily: FONTS.ui,
      fontSize: '18px',
      color: '#102033',
      fontStyle: '900',
      backgroundColor: '#ffcf3f',
      padding: { x: 18, y: 9 },
    }).setOrigin(0.5).setDepth(130).setVisible(false);

    this.toastText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 82, '', {
      fontFamily: FONTS.ui,
      fontSize: '14px',
      color: '#ffffff',
      backgroundColor: 'rgba(18,32,51,0.92)',
      padding: { x: 14, y: 8 },
      wordWrap: { width: 560, useAdvancedWrap: true },
      align: 'center',
    }).setOrigin(0.5, 0).setAlpha(0).setDepth(260);
  }

  private refreshLessonPanel(): void {
    this.lessonPanel.removeAll(true);
    const lesson = this.curriculum.currentLesson();
    if (!lesson) return;

    const w = 620;
    const h = 108;
    this.lessonPanel.add(this.add.rectangle(0, 0, w, h, FLOOR.panel, 0.97)
      .setStrokeStyle(1, FLOOR.panelStroke)
      .setOrigin(0, 0));

    this.lessonPanel.add(this.add.text(16, 12, `Lesson ${fmtLessonCount(this.curriculum.snapshot().lessonIndex)}`, {
      fontFamily: FONTS.ui,
      fontSize: '11px',
      color: toHexColor(FLOOR.muted),
      fontStyle: '800',
    }));

    this.lessonPanel.add(this.add.text(16, 31, lesson.title, {
      fontFamily: FONTS.ui,
      fontSize: '18px',
      color: toHexColor(FLOOR.ink),
      fontStyle: '800',
    }));

    this.lessonPanel.add(this.add.text(204, 16, lesson.objective, {
      fontFamily: FONTS.ui,
      fontSize: '14px',
      color: toHexColor(FLOOR.ink),
      wordWrap: { width: 392, useAdvancedWrap: true },
      lineSpacing: 3,
    }));

    this.lessonPanel.add(this.add.text(204, 56, `Jamie: ${lesson.mentorLine}`, {
      fontFamily: FONTS.ui,
      fontSize: '11px',
      color: toHexColor(FLOOR.muted),
      wordWrap: { width: 392, useAdvancedWrap: true },
      lineSpacing: 2,
    }));

    this.lessonPanel.add(this.add.rectangle(188, 14, 1, 80, 0xd6dee8).setOrigin(0, 0));
    this.lessonPanel.add(this.add.text(16, 57, 'Jamie is guiding this shift.', {
      fontFamily: FONTS.ui,
      fontSize: '11px',
      color: toHexColor(FLOOR.muted),
      fontStyle: '800',
    }));
  }

  private refreshGlossaryPanel(): void {
    this.glossaryPanel.removeAll(true);
    const w = 332;
    const h = 108;
    this.glossaryPanel.add(this.add.rectangle(0, 0, w, h, FLOOR.panel, 0.97)
      .setStrokeStyle(1, FLOOR.panelStroke)
      .setOrigin(0, 0));
    this.glossaryPanel.add(this.add.text(14, 14, 'Field Guide', {
      fontFamily: FONTS.ui,
      fontSize: '16px',
      color: toHexColor(FLOOR.ink),
      fontStyle: '800',
    }));
    this.glossaryPanel.add(this.add.text(w - 14, 17, `${this.learnedTerms.size}/${CORE_FLOOR_TERMS.length}`, {
      fontFamily: FONTS.ui,
      fontSize: '13px',
      color: toHexColor(FLOOR.muted),
      fontStyle: '800',
    }).setOrigin(1, 0));

    const terms = [...this.learnedTerms].slice(-3);
    if (terms.length === 0) {
      this.glossaryPanel.add(this.add.text(14, 44, 'Terms appear here as you learn them by doing the work.', {
        fontFamily: FONTS.ui,
        fontSize: '12px',
        color: toHexColor(FLOOR.muted),
        wordWrap: { width: w - 28, useAdvancedWrap: true },
      }));
      return;
    }

    let x = 14;
    for (const term of terms) {
      const label = this.add.text(x, 48, displayTermTitle(term), {
        fontFamily: FONTS.ui,
        fontSize: '12px',
        color: toHexColor(FLOOR.ink),
        fontStyle: '800',
      });
      this.glossaryPanel.add(label);
      x += label.width + 16;
    }
  }

  private spawnCurrentWorkItem(): void {
    this.currentItem?.container.destroy();
    this.currentItem = null;
    this.heldItem = null;

    const lesson = this.curriculum.currentLesson();
    if (!lesson) return;

    const item = this.createWorkItem(lesson, WORK_TRAY.x, WORK_TRAY.y - 4);
    this.currentItem = item;
  }

  private createWorkItem(lesson: FloorLesson, x: number, y: number): WorkItemView {
    const c = this.add.container(x, y).setDepth(70);
    c.setData('homeX', x);
    c.setData('homeY', y);

    const color = this.colorForWorkKind(lesson.workItem.kind);
    c.add(this.add.rectangle(5, 8, 92, 66, FLOOR.shadow, 0.16).setOrigin(0.5));
    c.add(this.add.rectangle(0, 0, 92, 66, 0xffffff)
      .setStrokeStyle(3, color)
      .setOrigin(0.5));
    c.add(this.add.rectangle(0, -23, 80, 7, color).setOrigin(0.5));
    c.add(this.add.image(-28, -2, 'icon:file-text').setDisplaySize(22, 22).setTint(color));
    c.add(this.add.text(-10, -11, lesson.workItem.title, {
      fontFamily: FONTS.ui,
      fontSize: '10px',
      color: toHexColor(FLOOR.ink),
      fontStyle: '800',
    }).setOrigin(0, 0.5));
    c.add(this.add.text(-36, 8, lesson.workItem.customer, {
      fontFamily: FONTS.ui,
      fontSize: '10px',
      color: toHexColor(FLOOR.muted),
    }).setOrigin(0, 0.5));
    c.add(this.add.text(0, 25, lesson.workItem.amountLabel, {
      fontFamily: FONTS.ui,
      fontSize: '11px',
      color: toHexColor(FLOOR.ink),
      fontStyle: '800',
    }).setOrigin(0.5));

    this.tweens.add({
      targets: c,
      y: y - 5,
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    return { lessonId: lesson.id, container: c };
  }

  private colorForWorkKind(kind: FloorLesson['workItem']['kind']): number {
    switch (kind) {
      case 'contract': return 0x6c63ff;
      case 'open-invoice':
      case 'aged-invoice': return 0xf59e0b;
      case 'payment': return 0x22c55e;
      case 'short-pay': return 0xf97316;
      case 'dispute': return 0xef4444;
      case 'call-note': return 0x0ea5e9;
      case 'close-card': return 0x14b8a6;
    }
  }

  private updateMovement(delta: number): void {
    const seconds = delta / 1000;
    let dx = 0;
    let dy = 0;
    if (this.cursors.left.isDown || this.keys.A.isDown) dx -= 1;
    if (this.cursors.right.isDown || this.keys.D.isDown) dx += 1;
    if (this.cursors.up.isDown || this.keys.W.isDown) dy -= 1;
    if (this.cursors.down.isDown || this.keys.S.isDown) dy += 1;

    if (dx !== 0 && dy !== 0) {
      dx *= 0.707;
      dy *= 0.707;
    }

    if (dx !== 0 || dy !== 0) {
      this.player.x = Phaser.Math.Clamp(this.player.x + dx * PLAYER_SPEED * seconds, 36, GAME_WIDTH - 36);
      this.player.y = Phaser.Math.Clamp(this.player.y + dy * PLAYER_SPEED * seconds, 190, GAME_HEIGHT - 54);
      this.playerBody.setScale(1.03, 0.97);
      this.playerFace.x = dx >= 0 ? 8 : -8;
    } else {
      this.playerBody.setScale(1, 1);
    }
  }

  private handleActions(): void {
    const interactDown = this.keys.SPACE.isDown || this.keys.E.isDown;
    const dropDown = this.keys.Q.isDown;
    const escDown = this.keys.ESC.isDown;
    const interactPressed = interactDown && !this.lastInteractDown;
    const dropPressed = dropDown && !this.lastDropDown;
    const escPressed = escDown && !this.lastEscDown;
    this.lastInteractDown = interactDown;
    this.lastDropDown = dropDown;
    this.lastEscDown = escDown;

    if (escPressed) {
      this.scene.launch('PauseScene', { targetScene: 'ARFloorScene' });
      this.scene.pause();
      return;
    }

    if (dropPressed && this.heldItem) {
      this.dropHeldItem();
      return;
    }

    if (!interactPressed) return;

    if (!this.heldItem) {
      const item = this.nearWorkItem();
      if (item) this.pickUpItem(item);
      return;
    }

    const station = this.nearStation();
    if (!station) return;
    const lesson = this.curriculum.currentLesson();
    if (!lesson) return;

    if (!this.curriculum.canUseStation(station.id)) {
      this.flashStation(station, false);
      this.showToast(`That work belongs at ${this.stationLabel(lesson.stationId)}.`);
      return;
    }

    this.openChallenge(lesson);
  }

  private updateHeldItemPosition(): void {
    if (!this.heldItem) return;
    this.heldItem.container.setPosition(this.player.x + 38, this.player.y - 34);
    this.heldItem.container.setScale(0.9);
    this.heldItem.container.setDepth(95);
  }

  private updateStationHighlights(): void {
    const lesson = this.curriculum.currentLesson();
    const showTarget = Boolean(lesson && !this.modal && !this.debriefOverlay && !this.completionOverlay);
    const target = showTarget && lesson ? this.stations.get(lesson.stationId) : null;
    if (target) {
      this.targetMarker.setVisible(true);
      this.targetMarker.setPosition(target.x, target.y + target.height / 2 + 44);
    } else {
      this.targetMarker.setVisible(false);
    }
    for (const station of this.stations.values()) {
      const isTarget = showTarget && lesson?.stationId === station.id;
      const isNear = this.distanceTo(station.x, station.y) <= INTERACT_DISTANCE;
      station.targetRing.setVisible(isTarget);
      station.targetRing.setStrokeStyle(5, FLOOR.target, isTarget ? 0.75 : 0);
      station.outline.setStrokeStyle(isNear ? 5 : 3, isTarget ? FLOOR.target : station.color);
    }
  }

  private updateActionHint(): void {
    if (this.modal || this.debriefOverlay || this.completionOverlay) {
      this.actionText.setText('');
      this.actionText.setVisible(false);
      return;
    }

    const lesson = this.curriculum.currentLesson();
    if (!lesson) {
      this.actionText.setText('');
      this.actionText.setVisible(false);
      return;
    }

    if (!this.heldItem && this.nearWorkItem()) {
      this.actionText.setText('SPACE / E: pick up work item');
      this.actionText.setVisible(true);
      return;
    }

    const station = this.nearStation();
    if (this.heldItem && station) {
      this.actionText.setText(
        station.id === lesson.stationId
          ? `SPACE / E: use ${this.stationLabel(station.id)}  |  Q: drop`
          : `Wrong station. Go to ${this.stationLabel(lesson.stationId)}  |  Q: drop`,
      );
      this.actionText.setVisible(true);
      return;
    }

    if (this.heldItem) {
      this.actionText.setText(`Carry this to ${this.stationLabel(lesson.stationId)}  |  Q: drop`);
      this.actionText.setVisible(true);
      return;
    }

    this.actionText.setText('Move with WASD or arrow keys. SPACE / E interacts.');
    this.actionText.setVisible(true);
  }

  private nearWorkItem(): WorkItemView | null {
    if (!this.currentItem || this.heldItem) return null;
    const c = this.currentItem.container;
    return this.distanceTo(c.x, c.y) <= WORK_ITEM_PICKUP_DISTANCE ? this.currentItem : null;
  }

  private nearStation(): StationView | null {
    let nearest: StationView | null = null;
    let nearestDistance = Number.POSITIVE_INFINITY;
    for (const station of this.stations.values()) {
      const d = this.distanceTo(station.x, station.y);
      if (d < nearestDistance && d <= INTERACT_DISTANCE) {
        nearest = station;
        nearestDistance = d;
      }
    }
    return nearest;
  }

  private distanceTo(x: number, y: number): number {
    return Phaser.Math.Distance.Between(this.player.x, this.player.y, x, y);
  }

  private pickUpItem(item: WorkItemView): void {
    this.heldItem = item;
    this.tweens.killTweensOf(item.container);
    this.tweens.add({
      targets: item.container,
      scale: 0.9,
      duration: 120,
      ease: 'Back.easeOut',
    });
    this.showToast('Good. Now carry it to the highlighted station.');
  }

  private dropHeldItem(): void {
    if (!this.heldItem) return;
    const item = this.heldItem;
    this.heldItem = null;
    item.container.setDepth(70);
    item.container.setScale(1);
    item.container.setPosition(this.player.x, this.player.y + 44);
    this.showToast('Dropped. Pick it back up when you are ready.');
  }

  private handleModalShortcuts(): void {
    if (!this.modalChoiceHandler) return;
    const index = Phaser.Input.Keyboard.JustDown(this.keys.ONE)
      ? 0
      : Phaser.Input.Keyboard.JustDown(this.keys.TWO)
        ? 1
        : Phaser.Input.Keyboard.JustDown(this.keys.THREE)
          ? 2
          : -1;
    const choice = index >= 0 ? this.modalChoices[index] : undefined;
    if (choice) this.modalChoiceHandler(choice);
  }

  private handleDebriefShortcuts(): void {
    if (!this.debriefContinue) return;
    if (Phaser.Input.Keyboard.JustDown(this.keys.SPACE) || Phaser.Input.Keyboard.JustDown(this.keys.E)) {
      this.debriefContinue();
    }
  }

  private openChallenge(lesson: FloorLesson): void {
    if (!this.heldItem || this.modal) return;

    this.tweens.killTweensOf(this.toastText);
    this.toastText.setAlpha(0);
    this.toastText.setText('');
    this.heldItem.container.setVisible(false);

    const root = this.add.container(0, 0).setDepth(DEPTHS.modal + 20);
    this.modal = root;

    root.add(this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.45)
      .setOrigin(0, 0)
      .setInteractive());

    const w = 620;
    const h = 450;
    const x = Math.round((GAME_WIDTH - w) / 2);
    const y = Math.round((GAME_HEIGHT - h) / 2);

    root.add(this.add.rectangle(x, y, w, h, FLOOR.panel)
      .setStrokeStyle(2, this.stations.get(lesson.stationId)?.color ?? FLOOR.wallTrim)
      .setOrigin(0, 0)
      .setInteractive());

    root.add(this.add.text(x + 26, y + 22, lesson.challengeTitle, {
      fontFamily: FONTS.ui,
      fontSize: '24px',
      color: toHexColor(FLOOR.ink),
      fontStyle: '800',
    }));

    root.add(this.add.text(x + 26, y + 62, `${lesson.workItem.customer} | ${lesson.workItem.amountLabel}`, {
      fontFamily: FONTS.ui,
      fontSize: '13px',
      color: toHexColor(FLOOR.muted),
      fontStyle: '800',
    }));

    root.add(this.add.text(x + 26, y + 92, lesson.challengePrompt, {
      fontFamily: FONTS.ui,
      fontSize: '15px',
      color: toHexColor(FLOOR.ink),
      wordWrap: { width: w - 52, useAdvancedWrap: true },
      lineSpacing: 4,
    }));

    this.modalChoices = lesson.choices;
    this.modalChoiceHandler = (selected) => {
      if (!selected.correct) {
        this.showToast(selected.detail);
        return;
      }
      const completion = this.curriculum.completeCurrent(selected.id);
      if (completion) this.finishLesson(completion);
    };

    let choiceY = y + 154;
    for (const [choiceIndex, choice] of lesson.choices.entries()) {
      this.renderChoice(root, x + 26, choiceY, w - 52, choiceIndex, choice, this.modalChoiceHandler);
      choiceY += 82;
    }

    root.add(this.add.text(x + 26, y + h - 36, 'Choose with 1 / 2 / 3 or click the action that keeps AR accurate.', {
      fontFamily: FONTS.ui,
      fontSize: '12px',
      color: toHexColor(FLOOR.muted),
      fontStyle: 'italic',
    }));
  }

  private renderChoice(
    root: Phaser.GameObjects.Container,
    x: number,
    y: number,
    width: number,
    choiceIndex: number,
    choice: FloorChoice,
    onSelect: (choice: FloorChoice) => void,
  ): void {
    const row = this.add.rectangle(x, y, width, 68, 0xf4f7fb)
      .setStrokeStyle(1, 0xc9d5e3)
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    root.add(row);

    root.add(this.add.circle(x + 23, y + 23, 13, 0xffffff)
      .setStrokeStyle(2, 0xc9d5e3));
    root.add(this.add.text(x + 23, y + 23, `${choiceIndex + 1}`, {
      fontFamily: FONTS.ui,
      fontSize: '13px',
      color: toHexColor(FLOOR.muted),
      fontStyle: '900',
    }).setOrigin(0.5));

    const label = this.add.text(x + 48, y + 13, choice.label, {
      fontFamily: FONTS.ui,
      fontSize: '15px',
      color: toHexColor(FLOOR.ink),
      fontStyle: '800',
    });
    root.add(label);

    const detail = this.add.text(x + 48, y + 38, choice.detail, {
      fontFamily: FONTS.ui,
      fontSize: '12px',
      color: toHexColor(FLOOR.muted),
      wordWrap: { width: width - 66, useAdvancedWrap: true },
    });
    root.add(detail);

    row.on('pointerover', () => row.setFillStyle(0xe8f1ff));
    row.on('pointerout', () => row.setFillStyle(0xf4f7fb));
    row.on('pointerdown', () => {
      if (!choice.correct) {
        row.setStrokeStyle(2, FLOOR.danger);
        this.time.delayedCall(240, () => row.setStrokeStyle(1, 0xc9d5e3));
      }
      onSelect(choice);
    });
  }

  private finishLesson(completion: LessonCompletion): void {
    const oldItem = this.heldItem ?? this.currentItem;
    this.heldItem = null;
    this.currentItem = null;

    if (oldItem) {
      this.tweens.add({
        targets: oldItem.container,
        scale: 0,
        alpha: 0,
        duration: 180,
        ease: 'Back.easeIn',
        onComplete: () => oldItem.container.destroy(),
      });
    }

    this.modal?.destroy();
    this.modal = null;
    this.modalChoices = [];
    this.modalChoiceHandler = null;

    this.flashStation(this.stations.get(completion.completedLesson.stationId) ?? null, true);

    completion.unlockedTerms.forEach((term) => {
      this.learnedTerms.add(term);
      this.showTermUnlocked(term, completion.completedLesson.id);
    });
    this.refreshGlossaryPanel();
    this.showDebriefOverlay(completion);
  }

  private showDebriefOverlay(completion: LessonCompletion): void {
    if (this.debriefOverlay) return;

    this.tweens.killTweensOf(this.toastText);
    this.toastText.setAlpha(0);
    this.toastText.setText('');
    this.actionText.setText('');
    this.actionText.setVisible(false);

    const unlockedTerms = completion.unlockedTerms;
    const root = this.add.container(0, 0).setDepth(DEPTHS.modal + 30);
    this.debriefOverlay = root;

    root.add(this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x07111f, 0.5)
      .setOrigin(0, 0)
      .setInteractive());

    const w = 700;
    const h = unlockedTerms.length >= 3 ? 446 : unlockedTerms.length === 2 ? 406 : 366;
    const x = Math.round((GAME_WIDTH - w) / 2);
    const y = Math.round((GAME_HEIGHT - h) / 2);

    root.add(this.add.rectangle(x + 8, y + 10, w, h, 0x000000, 0.24).setOrigin(0, 0));
    root.add(this.add.rectangle(x, y, w, h, FLOOR.panel)
      .setStrokeStyle(3, this.stations.get(completion.completedLesson.stationId)?.color ?? FLOOR.wallTrim)
      .setOrigin(0, 0)
      .setInteractive());

    root.add(this.add.text(x + 26, y + 24, 'Clean job', {
      fontFamily: FONTS.ui,
      fontSize: '28px',
      color: toHexColor(FLOOR.ink),
      fontStyle: '900',
    }));
    root.add(this.add.text(x + w - 28, y + 30, `Job ${this.curriculum.snapshot().completedLessonIds.length}/${AR_FLOOR_LESSONS.length}`, {
      fontFamily: FONTS.ui,
      fontSize: '13px',
      color: toHexColor(FLOOR.muted),
      fontStyle: '900',
    }).setOrigin(1, 0));

    root.add(this.add.text(x + 26, y + 66, completion.completedLesson.successLine, {
      fontFamily: FONTS.ui,
      fontSize: '15px',
      color: toHexColor(FLOOR.ink),
      wordWrap: { width: w - 52, useAdvancedWrap: true },
      lineSpacing: 4,
    }));

    root.add(this.add.text(x + 26, y + 116, 'What this means in AR', {
      fontFamily: FONTS.ui,
      fontSize: '15px',
      color: toHexColor(FLOOR.ink),
      fontStyle: '900',
    }));

    const rowH = unlockedTerms.length >= 3 ? 58 : 62;
    let termY = y + 148;
    for (const term of unlockedTerms) {
      const copy = FLOOR_TERM_COPY[term];
      root.add(this.add.circle(x + 38, termY + 13, 6, FLOOR.success));
      root.add(this.add.text(x + 54, termY, displayTermTitle(term), {
        fontFamily: FONTS.ui,
        fontSize: '14px',
        color: toHexColor(FLOOR.ink),
        fontStyle: '900',
      }));
      root.add(this.add.text(x + 54, termY + 21, copy.definition, {
        fontFamily: FONTS.ui,
        fontSize: '12px',
        color: toHexColor(FLOOR.muted),
        wordWrap: { width: w - 92, useAdvancedWrap: true },
        lineSpacing: 2,
      }));
      termY += rowH;
    }

    const nextText = completion.nextLesson
      ? `Next job: ${completion.nextLesson.title}.`
      : 'Next: review the whole AR loop.';
    root.add(this.add.text(x + 26, y + h - 86, nextText, {
      fontFamily: FONTS.ui,
      fontSize: '13px',
      color: toHexColor(FLOOR.muted),
      fontStyle: '800',
      wordWrap: { width: w - 52, useAdvancedWrap: true },
    }));

    const buttonLabel = completion.isComplete ? 'See Summary' : 'Next Job';
    const button = this.add.rectangle(GAME_WIDTH / 2, y + h - 42, 240, 44, FLOOR.target)
      .setStrokeStyle(3, FLOOR.ink)
      .setInteractive({ useHandCursor: true });
    root.add(button);
    root.add(this.add.text(GAME_WIDTH / 2, y + h - 42, `${buttonLabel}  SPACE / E`, {
      fontFamily: FONTS.ui,
      fontSize: '16px',
      color: toHexColor(FLOOR.ink),
      fontStyle: '900',
    }).setOrigin(0.5));

    const dismiss = (): void => {
      const overlay = this.debriefOverlay;
      this.debriefOverlay = null;
      this.debriefContinue = null;
      overlay?.destroy();
      if (completion.isComplete) {
        this.showCompletionOverlay();
        return;
      }
      this.refreshLessonPanel();
      this.spawnCurrentWorkItem();
      this.showToast('Next job is in the work tray.');
    };

    this.debriefContinue = dismiss;
    button.on('pointerdown', dismiss);
  }

  private showTermUnlocked(term: FloorTermId, lessonId: string): void {
    if (isCoreSaveTerm(term)) {
      const fictionalMinutes = 540 + Math.max(0, this.curriculum.snapshot().lessonIndex - 1) * 45;
      this.saveStore.recordUnlock(term, fictionalMinutes, lessonId);
    }

    this.pulseGlossaryPanel();
  }

  private pulseGlossaryPanel(): void {
    this.tweens.killTweensOf(this.glossaryPanel);
    this.tweens.add({
      targets: this.glossaryPanel,
      scale: 1.035,
      duration: 120,
      yoyo: true,
      repeat: 1,
      ease: 'Sine.easeInOut',
      onComplete: () => this.glossaryPanel.setScale(1),
    });
  }

  private showWelcomeOverlay(): void {
    if (this.welcomeOverlay) return;
    const root = this.add.container(0, 0).setDepth(DEPTHS.modal + 40);
    this.welcomeOverlay = root;

    root.add(this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x07111f, 0.7)
      .setOrigin(0, 0)
      .setInteractive());

    const w = 680;
    const h = 450;
    const x = Math.round((GAME_WIDTH - w) / 2);
    const y = Math.round((GAME_HEIGHT - h) / 2);
    root.add(this.add.rectangle(x + 8, y + 10, w, h, 0x000000, 0.28).setOrigin(0, 0));
    root.add(this.add.rectangle(x, y, w, h, 0xfffbf1)
      .setStrokeStyle(4, FLOOR.wallTrim)
      .setOrigin(0, 0));

    root.add(this.add.text(GAME_WIDTH / 2, y + 46, 'Welcome to the AR Floor', {
      fontFamily: FONTS.ui,
      fontSize: '34px',
      color: toHexColor(FLOOR.ink),
      fontStyle: '900',
    }).setOrigin(0.5));

    root.add(this.add.text(GAME_WIDTH / 2, y + 82, 'AR means accounts receivable: money customers owe after we send invoices. This training shift teaches the loop by having you move the work yourself.', {
      fontFamily: FONTS.ui,
      fontSize: '15px',
      color: toHexColor(FLOOR.muted),
      align: 'center',
      wordWrap: { width: w - 80, useAdvancedWrap: true },
      lineSpacing: 4,
    }).setOrigin(0.5, 0));

    const steps = [
      { icon: '1', title: 'Pick up the work item', body: 'Walk to the yellow Work Tray. Press SPACE or E.' },
      { icon: '2', title: 'Run to the highlighted station', body: 'Follow the big GO HERE arrow and yellow outline.' },
      { icon: '3', title: 'Make the AR call', body: 'Choose the answer that keeps cash and invoices accurate.' },
    ];

    steps.forEach((step, i) => {
      const sx = x + 110 + i * 200;
      const sy = y + 198;
      root.add(this.add.circle(sx, sy, 26, FLOOR.target).setStrokeStyle(3, FLOOR.ink));
      root.add(this.add.text(sx, sy, step.icon, {
        fontFamily: FONTS.ui,
        fontSize: '22px',
        color: toHexColor(FLOOR.ink),
        fontStyle: '900',
      }).setOrigin(0.5));
      root.add(this.add.text(sx, sy + 42, step.title, {
        fontFamily: FONTS.ui,
        fontSize: '15px',
        color: toHexColor(FLOOR.ink),
        fontStyle: '900',
        wordWrap: { width: 150, useAdvancedWrap: true },
        align: 'center',
      }).setOrigin(0.5, 0));
      root.add(this.add.text(sx, sy + 88, step.body, {
        fontFamily: FONTS.ui,
        fontSize: '12px',
        color: toHexColor(FLOOR.muted),
        wordWrap: { width: 150, useAdvancedWrap: true },
        align: 'center',
      }).setOrigin(0.5, 0));
    });

    root.add(this.add.text(GAME_WIDTH / 2, y + h - 96, 'Controls: WASD / arrows to move   |   SPACE / E to interact   |   Q to drop', {
      fontFamily: FONTS.ui,
      fontSize: '14px',
      color: toHexColor(FLOOR.ink),
      fontStyle: '900',
    }).setOrigin(0.5));

    const start = this.add.rectangle(GAME_WIDTH / 2, y + h - 46, 250, 46, FLOOR.target)
      .setStrokeStyle(3, FLOOR.ink)
      .setInteractive({ useHandCursor: true });
    root.add(start);
    root.add(this.add.text(GAME_WIDTH / 2, y + h - 46, 'Start Shift', {
      fontFamily: FONTS.ui,
      fontSize: '18px',
      color: toHexColor(FLOOR.ink),
      fontStyle: '900',
    }).setOrigin(0.5));

    const dismiss = (): void => {
      const overlay = this.welcomeOverlay;
      this.welcomeOverlay = null;
      if (overlay) {
        this.tweens.add({
          targets: overlay,
          alpha: 0,
          duration: 180,
          onComplete: () => overlay.destroy(),
        });
      }
      this.showToast('Step 1: walk to START HERE and press SPACE / E to pick up the contract.');
    };
    start.on('pointerdown', dismiss);
    this.input.keyboard?.once('keydown-SPACE', dismiss);
    this.input.keyboard?.once('keydown-E', dismiss);
  }

  private showCompletionOverlay(): void {
    if (this.completionOverlay) return;
    if (!this.runRecorded) {
      this.saveStore.recordRunComplete(120, 42);
      this.runRecorded = true;
    }
    this.tweens.killTweensOf(this.toastText);
    this.toastText.setAlpha(0);
    this.toastText.setText('');
    this.actionText.setText('');
    this.actionText.setVisible(false);

    const root = this.add.container(0, 0).setDepth(DEPTHS.ending);
    this.completionOverlay = root;
    root.add(this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x102033, 0.94)
      .setOrigin(0, 0)
      .setInteractive());

    root.add(this.add.text(GAME_WIDTH / 2, 92, 'You built the AR loop.', {
      fontFamily: FONTS.ui,
      fontSize: '38px',
      color: '#ffffff',
      fontStyle: '800',
    }).setOrigin(0.5));

    root.add(this.add.text(GAME_WIDTH / 2, 140, 'Invoice -> aging -> dunning -> payment -> exceptions -> cash health', {
      fontFamily: FONTS.ui,
      fontSize: '16px',
      color: '#b9cfe2',
      fontStyle: '700',
    }).setOrigin(0.5));

    this.renderSummaryBoard(root);

    root.add(this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 152, 'This is the manual AR loop. Fazeshift exists to make it less manual.', {
      fontFamily: FONTS.ui,
      fontSize: '18px',
      color: '#ffffff',
      align: 'center',
      wordWrap: { width: 720, useAdvancedWrap: true },
    }).setOrigin(0.5));

    this.renderEndButton(root, GAME_WIDTH / 2 - 128, GAME_HEIGHT - 82, 'Back to Menu', () => {
      this.scene.start('MenuScene');
    });
    this.renderEndButton(root, GAME_WIDTH / 2 + 128, GAME_HEIGHT - 82, 'Advanced Desktop', () => {
      this.scene.start('ColdOpenScene', { mode: 'endless' });
    });
  }

  private renderSummaryBoard(root: Phaser.GameObjects.Container): void {
    const boardX = 150;
    const boardY = 202;
    const boardW = GAME_WIDTH - 300;
    const boardH = 330;

    root.add(this.add.rectangle(boardX, boardY, boardW, boardH, 0xffffff, 0.98)
      .setStrokeStyle(2, 0x42627f)
      .setOrigin(0, 0));

    root.add(this.add.text(boardX + 24, boardY + 22, 'Terms learned', {
      fontFamily: FONTS.ui,
      fontSize: '20px',
      color: toHexColor(FLOOR.ink),
      fontStyle: '800',
    }));

    const terms = CORE_FLOOR_TERMS.filter((t) => this.learnedTerms.has(t));
    const colW = (boardW - 48) / 2;
    terms.forEach((term, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const x = boardX + 24 + col * colW;
      const y = boardY + 64 + row * 38;
      root.add(this.add.circle(x + 8, y + 9, 5, FLOOR.success));
      root.add(this.add.text(x + 22, y, displayTermTitle(term), {
        fontFamily: FONTS.ui,
        fontSize: '13px',
        color: toHexColor(FLOOR.ink),
        fontStyle: '800',
        wordWrap: { width: colW - 42, useAdvancedWrap: true },
      }));
    });

    const dsoX = boardX + boardW - 170;
    const dsoY = boardY + 20;
    root.add(this.add.rectangle(dsoX, dsoY, 140, 58, 0xe8f7f7)
      .setStrokeStyle(1, 0x8edbd4)
      .setOrigin(0, 0));
    root.add(this.add.text(dsoX + 14, dsoY + 10, 'DSO today', {
      fontFamily: FONTS.ui,
      fontSize: '12px',
      color: toHexColor(FLOOR.muted),
      fontStyle: '800',
    }));
    root.add(this.add.text(dsoX + 14, dsoY + 28, '42 days', {
      fontFamily: FONTS.ui,
      fontSize: '20px',
      color: toHexColor(FLOOR.ink),
      fontStyle: '800',
    }));
  }

  private renderEndButton(
    root: Phaser.GameObjects.Container,
    x: number,
    y: number,
    label: string,
    onClick: () => void,
  ): void {
    const w = 210;
    const h = 46;
    const bg = this.add.rectangle(x, y, w, h, FLOOR.wallTrim)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    root.add(bg);
    root.add(this.add.text(x, y, label, {
      fontFamily: FONTS.ui,
      fontSize: '15px',
      color: '#102033',
      fontStyle: '800',
    }).setOrigin(0.5));

    bg.on('pointerover', () => bg.setFillStyle(0x8ce5df));
    bg.on('pointerout', () => bg.setFillStyle(FLOOR.wallTrim));
    bg.on('pointerdown', onClick);
  }

  private showToast(message: string): void {
    this.toastText.setText(message);
    this.tweens.killTweensOf(this.toastText);
    this.toastText.setAlpha(0);
    this.tweens.add({
      targets: this.toastText,
      alpha: 1,
      duration: 140,
      onComplete: () => {
        this.time.delayedCall(2300, () => {
          this.tweens.add({ targets: this.toastText, alpha: 0, duration: 220 });
        });
      },
    });
  }

  private flashStation(station: StationView | null, success: boolean): void {
    if (!station) return;
    const target = station.container;
    this.tweens.add({
      targets: target,
      scale: success ? 1.08 : 0.96,
      duration: 90,
      yoyo: true,
      repeat: success ? 1 : 3,
      ease: 'Sine.easeInOut',
    });
    station.outline.setStrokeStyle(5, success ? FLOOR.success : FLOOR.danger);
    this.time.delayedCall(380, () => {
      station.outline.setStrokeStyle(3, station.color);
    });
  }

  private stationLabel(stationId: FloorStationId): string {
    return STATIONS.find((s) => s.id === stationId)?.label ?? stationId;
  }
}
